import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep, groupBy, keys, orderBy } from 'lodash';
import {
  Text,
  Box,
  Flex,
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Input,
  Icon,
  useDisclosure,
  Tooltip,
  Badge,
  Divider,
  Button,
  Switch,
} from '@chakra-ui/react';
import { TAdvancedColumnData, TColumnData, TInput, TTab } from './type';
import { CirlceIcon, NormalIcon, allIcons } from '../utils/Icon';
import { TState, useActions } from '../../redux/store';
import DragDropList from '../dragAndDrop/DragDropList';
import { isEmpty, regCompare } from '../../helpers';
import DropElement from '../dragAndDrop/DropElement';

type TEditColumns = {
  tab: TTab;
  setAdvancedColumns: React.Dispatch<
    React.SetStateAction<TAdvancedColumnData[]>
  >;
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>;
};

const EMPTY_ITEM = {
  _id: 'new',
  label: 'NEW',
  type: 'empty',
};

const ToggleButton = ({ label }: { label: string }) => {
  const { isOpen, onToggle } = useDisclosure();
  const theme = useSelector((state: TState) => state.theme);

  return (
    <AccordionButton textTransform='capitalize' textAlign='left'>
      <Text flexGrow={1}>{label}</Text>
      <Icon
        onClick={onToggle}
        color={theme.dark}
        as={isOpen ? allIcons.HiChevronUp : allIcons.HiChevronDown}
        boxSize={15}
      />
    </AccordionButton>
  );
};

const InputBadge = ({
  col,
  onClick,
  onDelete,
}: {
  col: TInput | TColumnData;
  onClick?: () => void;
  onDelete?: () => void;
}) => {
  const theme = useSelector((state: TState) => state.theme);
  const isComm = col._id === 'crm_comm';
  const label = `${col.type === 'address' && !/adresse/gi.test(col.label || col.name_id || '') ? 'Adresse ' : ''}${col.label || col.name_id || ''}`;

  return !col ? null : col._id === 'new' && col.type === 'empty' ? (
    <Box
      borderStyle='dashed'
      w='50px'
      h='28px'
      borderColor='#000'
      borderWidth='1px'
      borderRadius='5px'
    ></Box>
  ) : (
    <Tooltip label={`${col.description || col.name_id || ''} / ${col.type}`}>
      <Badge
        key={col._id}
        display='flex'
        borderRadius={5}
        p={1}
        cursor='pointer'
        backgroundColor={isComm ? theme.dark : theme.lighter}
        color={isComm ? theme.none : undefined}
        _hover={{ backgroundColor: theme.normal }}
        onClick={onClick}
      >
        <Text
          textTransform='uppercase'
          overflow='hidden'
          whiteSpace='nowrap'
          textOverflow='ellipsis'
          flexGrow={1}
          h='20px'
        >
          {label}
        </Text>
        {!isComm && onDelete && (
          <NormalIcon
            boxSize='20px'
            ml={1}
            as={allIcons.HiMiniXMark}
            onClick={onDelete}
          />
        )}
      </Badge>
    </Tooltip>
  );
};

const ChildList = ({
  advancedColumns,
  groupIdx,
  selectedGroup,
  noTitle,
  onMove,
  onDrop,
  onTitleUpdate,
  handleDelete,
  setSelectedGroup,
}: {
  advancedColumns: TAdvancedColumnData;
  groupIdx: number;
  selectedGroup: boolean;
  noTitle?: boolean;
  onMove: (dragIndex: number, dropIndex: number) => void;
  onDrop: () => void;
  onTitleUpdate: (title: string, containerId: number) => void;
  handleDelete: (containerId: number, col?: TColumnData) => void;
  setSelectedGroup: (groupId: number) => void;
}) => {
  const theme = useSelector((state: TState) => state.theme);
  const [isEdit, setIsEdit] = useState(false);

  return !advancedColumns.tableColumns?.length ? null : (
    <Flex
      borderColor={theme.dark}
      bgColor={selectedGroup ? theme.light : theme.lighter}
      borderWidth='1px'
      borderRadius='5px'
      p={2}
      minH='20px'
      mb={2}
      gap={2}
      flexWrap='wrap'
      direction='row'
      onClick={() => setSelectedGroup(groupIdx)}
    >
      <Flex w='100%' alignItems='center' gap={2}>
        {noTitle ? null : (
          <>
            {isEdit ? (
              <Input
                defaultValue={advancedColumns.title}
                onBlur={(e) => onTitleUpdate(e.target.value, groupIdx)}
              />
            ) : (
              advancedColumns.title || 'Pas de titre'
            )}
            <NormalIcon
              as={allIcons.HiPencilSquare}
              boxSize={5}
              onClick={() => setIsEdit((e) => !e)}
            />
          </>
        )}
        <Box flexGrow={1} />
        <Tooltip label='Tout supprimer'>
          <span>
            <NormalIcon
              as={allIcons.HiMiniXMark}
              boxSize={5}
              onClick={() => handleDelete(groupIdx)}
            />
          </span>
        </Tooltip>
      </Flex>
      {advancedColumns.tableColumns.map((item, idx) => (
        <DropElement
          id={`${idx}.${groupIdx}`}
          type={'TABLE_COLUMNS'}
          onMove={onMove}
          onDrop={onDrop}
          hasStyle
          style={{
            width:
              !noTitle && item?.type === 'address'
                ? '100%'
                : `calc(33% - .5rem)`,
          }}
        >
          <InputBadge
            col={item}
            onDelete={() => handleDelete(groupIdx, item)}
          />
        </DropElement>
      ))}
      <DropElement
        id={`${advancedColumns.tableColumns.length}.${groupIdx}`}
        type={'TABLE_COLUMNS'}
        onMove={onMove}
        onDrop={onDrop}
        noDrag
        hasStyle
      >
        <InputBadge col={EMPTY_ITEM} />
      </DropElement>
    </Flex>
  );
};

const EditColumns = ({ setAdvancedColumns, setHasChanges }: TEditColumns) => {
  const dataInputs = useSelector((state: TState) => state.dataInputs);
  const tabToUpdate = useSelector((state: TState) => state.tabToUpdate);
  const theme = useSelector((state: TState) => state.theme);

  const { setModalType } = useActions();

  const [groups, setGroups] = useState<TAdvancedColumnData[]>([]);
  const [selectedGroup, setSelectedGroup] = useState(0);
  const [filter, setFilter] = useState('');
  const [hasComm, setHasComm] = useState(false);

  const tempList = useRef<TAdvancedColumnData[]>();

  const activeInput =
    groups?.reduce(
      (prev: string[], curr) => [
        ...prev,
        ...(curr.tableColumns?.map((t) => t?.name_id || '') || []),
      ],
      [],
    ) || [];
  const inputs = dataInputs?.filter(
    (col) =>
      !activeInput.includes(`${col.name_id}`) &&
      col.name_id !== 'planning_type',
  );
  const inputsByGroup = groupBy(inputs, ({ group }) => (!group ? '' : group));
  const orderedKeys = orderBy(keys(inputsByGroup));

  useEffect(() => {
    setAdvancedColumns(groups.filter((f) => f?.tableColumns?.length));
  }, [groups]);

  useEffect(() => {
    const alreadySet = tabToUpdate?.tableColumns?.[0]?.tableColumns?.find(
      (c) => c._id === 'crm_comm',
    );

    setGroups(tabToUpdate?.tableColumns || []);
    setHasComm(!!alreadySet);
    setSelectedGroup(0);
  }, [tabToUpdate]);

  const handleCommChange = (e: any) => {
    setHasChanges(true);

    const isChecked = e.target.checked;
    setHasComm(isChecked);

    setGroups((g) =>
      isChecked
        ? [{ ...g[0], tableColumns: [...g[0].tableColumns, commBadge] }]
        : [
            {
              ...g[0],
              tableColumns: [
                ...g[0].tableColumns.filter((c) => c._id !== 'crm_comm'),
              ],
            },
          ],
    );
  };

  const handleDelete = (containerId: number, col?: TColumnData) => {
    setHasChanges(true);
    setGroups((gs) =>
      gs
        .map((g, idx) =>
          idx !== containerId
            ? g
            : !col
              ? { title: '', tableColumns: [] }
              : {
                  ...g,
                  tableColumns: g.tableColumns.filter(
                    (f) => f.name_id !== col.name_id,
                  ),
                },
        )
        .filter((f) => f?.tableColumns.length),
    );
  };

  const filterGroups = (groupsList: TAdvancedColumnData[]) => {
    const newGroups = cloneDeep(groupsList);
    return newGroups.filter((f) => f?.tableColumns.length);
  };

  const onMove = (dragIndex: number, dropIndex: number) => {
    if (isEmpty(dropIndex) || isEmpty(dragIndex)) return;

    if (!tempList.current) {
      tempList.current = groups;
    }

    const [dragId, dragContainer] = `${dragIndex}`.split('.');
    const [dropId, dropContainer] = `${dropIndex}`.split('.');

    const newGroups = cloneDeep(!tempList.current ? groups : tempList.current);

    const [item] = newGroups[Number(dragContainer)].tableColumns.splice(
      Number(dragId),
      1,
    );

    if (!newGroups[Number(dropContainer)]) {
      newGroups[Number(dropContainer)] = { title: '', tableColumns: [] };
    }

    newGroups[Number(dropContainer)].tableColumns.splice(
      Number(dropId),
      0,
      item,
    );

    setHasChanges(true);
    setGroups(newGroups);
  };

  const onDrop = () => {
    tempList.current = undefined as any;
    setHasChanges(true);
    setGroups((g) => filterGroups(g));
  };

  const onTitleUpdate = (title: string, containerId: number) => {
    setHasChanges(true);
    setGroups((g) => {
      const newGroups = cloneDeep(g);
      newGroups[containerId].title = title;
      return newGroups;
    });
  };

  const tableView = !tabToUpdate?.parent_id || tabToUpdate?.isMenu;
  const commBadge = {
    _id: 'crm_comm',
    label: 'COMMENTAIRES',
    name_id: 'crm_comm',
    type: 'comm',
  };

  const handleSetGroup = (col: TInput[]) => {
    setHasChanges(true);
    setGroups((c) => {
      const newCols = cloneDeep(c);

      if (!newCols[selectedGroup]) {
        newCols[selectedGroup] = {
          title: '',
          tableColumns: [],
        };
      }

      newCols[selectedGroup].tableColumns.push(...col);

      return newCols;
    });
  };

  return (
    <Flex h='100%' gap={3} overflow='hidden'>
      <Flex flex={1} direction='column'>
        <Box textAlign='center' position='relative'>
          <Text fontSize={26} fontWeight={500}>
            Liste des champs
          </Text>
          <Text fontStyle='italic' fontSize={theme.fontSize.small} mb={3}>
            Cliquer pour ajouter
          </Text>
          <Input
            onChange={(e) => setFilter(e.target.value)}
            placeholder='Filtrer les champs'
            mb={3}
          />
          <Box position={'absolute'} top={0} right={0}>
            <Tooltip label='Créer un nouveau champ'>
              <span>
                <CirlceIcon
                  as={allIcons.HiPlusSmall}
                  onClick={() => setModalType('addInput')}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Accordion
          allowMultiple
          index={filter ? orderedKeys.map((x, idx) => idx) : undefined}
          flexGrow={1}
          overflow='auto'
        >
          {orderedKeys.map((groupName) => (
            <AccordionItem>
              <Box position='relative'>
                <ToggleButton label={groupName || 'Champs de base'} />
                <Button
                  size='sm'
                  position='absolute'
                  top={1}
                  right={10}
                  onClick={() => handleSetGroup(inputsByGroup[groupName])}
                >
                  Tout ajouter
                </Button>
              </Box>
              <AccordionPanel display='flex' gap={2} flexWrap='wrap'>
                {inputsByGroup[groupName].map((col) =>
                  filter && !regCompare(filter, col.label, 'ig') ? null : (
                    <Box w={`calc(33% - .5rem)`}>
                      <InputBadge
                        col={col}
                        onClick={() => handleSetGroup([col])}
                      />
                    </Box>
                  ),
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Flex>
      <Divider orientation='vertical' />
      <Flex direction='column' flex={1}>
        <Box textAlign='center'>
          <Text fontSize={26} fontWeight={500}>
            Champs ajoutés
          </Text>
          <Text fontStyle='italic' fontSize={theme.fontSize.small} mb={3}>
            Déplacer pour changer l'ordre
          </Text>
        </Box>
        <Box flexGrow={1} overflow='auto'>
          <DragDropList
            listItem={groups}
            onDrop={(list) => {
              setHasChanges(true);
              setGroups(filterGroups(list));
            }}
            List={({ item, idx }) => (
              <ChildList
                advancedColumns={item}
                groupIdx={idx}
                selectedGroup={selectedGroup === idx}
                noTitle={tableView}
                onMove={onMove}
                onDrop={onDrop}
                onTitleUpdate={onTitleUpdate}
                handleDelete={handleDelete}
                setSelectedGroup={setSelectedGroup}
              />
            )}
            type='TABLE_COLUMNS_BLOCKS'
          />
          {tableView && (
            <Flex gap={2} alignItems='center'>
              Afficher les commentaires dans le tableau{' '}
              <Switch onChange={handleCommChange} isChecked={hasComm} />
            </Flex>
          )}
          {tableView ? null : (
            <Box
              bgColor={
                selectedGroup === (groups?.length || 0)
                  ? theme.light
                  : theme.lighter
              }
              borderWidth='1px'
              borderRadius='5px'
              onClick={() => setSelectedGroup(groups?.length || 0)}
              p={2}
            >
              <DropElement
                id={`0.${groups.length}`}
                type={'TABLE_COLUMNS'}
                onMove={onMove}
                onDrop={onDrop}
                noDrag
              >
                <InputBadge col={EMPTY_ITEM} />
              </DropElement>
            </Box>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default EditColumns;
